import { ArrowLeft, TriangleAlert, X } from 'lucide-react';
import React, { memo, useMemo, useCallback } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog.tsx';
import { Separator } from '@/components/ui/separator';
import type { IPurchaseRequestItemBody } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';

interface DuplicateItemDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  item: IPurchaseRequestItemBody | null;
  onConfirm: () => void;
  onCancel: () => void;
  confirmButtonRef: React.RefObject<HTMLButtonElement>;
  duplicateItem: IPurchaseRequestItemBody | null;
}

// Memoized comparison component for item details
const ComparisonRow = memo(
  ({
    label,
    currentValue,
    newValue,
    isDifferent,
  }: {
    label: string;
    currentValue: string | number | null | undefined;
    newValue: string | number | null | undefined;
    isDifferent: boolean;
  }) => {
    const displayCurrentValue =
      currentValue !== undefined && currentValue !== null
        ? typeof currentValue === 'number' &&
          label.includes('خصم') &&
          !label.includes('صافي')
          ? `${currentValue}%`
          : typeof currentValue === 'number' &&
              !isNaN(currentValue) &&
              label.includes('الإجمالي')
            ? currentValue.toFixed(2)
            : currentValue
        : '-';

    const displayNewValue =
      newValue !== undefined && newValue !== null
        ? typeof newValue === 'number' &&
          label.includes('خصم') &&
          !label.includes('صافي')
          ? `${newValue}%`
          : typeof newValue === 'number' &&
              !isNaN(newValue) &&
              label.includes('الإجمالي')
            ? newValue.toFixed(2)
            : newValue
        : '-';

    return (
      <p
        className={`mb-1 ${isDifferent ? (label === 'الصنف' ? '' : 'font-bold ' + (currentValue ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400')) : ''}`}
      >
        <strong>{label}:</strong>{' '}
        {label === 'الصنف' ? displayCurrentValue : displayNewValue}
      </p>
    );
  },
);
ComparisonRow.displayName = 'ComparisonRow';

// Memoized component for the item details box
const ItemDetailsBox = memo(
  ({
    title,
    item,
    compareItem,
    isNewItem,
  }: {
    title: string;
    item: IPurchaseRequestItemBody;
    compareItem: IPurchaseRequestItemBody | null;
    isNewItem: boolean;
  }) => {
    const itemDetails = useMemo(() => {
      const fields = [
        {
          key: 'ItemName',
          label: 'الصنف',
          value: item.ItemName || String(item.Item_ID),
          fallback: item.Item_ID,
        },
        { key: 'Qty', label: 'الكمية', value: item.Qty },
        { key: 'Price', label: 'السعر', value: item.Price },
        { key: 'Discount', label: 'الخصم', value: item.Discount },
        { key: 'BNO', label: 'الباتش', value: item.BNO || '-' },
        {
          key: 'ExpireDate',
          label: 'تاريخ الصلاحية',
          value: item.ExpireDate || '-',
        },
        { key: 'Bonus', label: 'البونص', value: item.Bonus || '0' },
        {
          key: 'FinishDiscount',
          label: 'صافي الخصم',
          value: item.FinishDiscount?.toFixed(2),
        },
        {
          key: 'TotalAfterDiscount',
          label: 'الإجمالي',
          value: item.TotalAfterDiscount?.toFixed(2),
        },
      ];

      return fields;
    }, [item]);

    return (
      <div className="rounded-md border border-gray-300 bg-gray-50 p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-2 font-bold">{title}</h3>
        {item ? (
          <>
            {itemDetails.map((field) => (
              <ComparisonRow
                key={field.key}
                label={field.label}
                currentValue={isNewItem ? field.value : field.value}
                newValue={
                  compareItem
                    ? isNewItem
                      ? compareItem[field.key as keyof IPurchaseRequestItemBody]
                      : field.value
                    : field.value
                }
                isDifferent={
                  compareItem
                    ? compareItem[
                        field.key as keyof IPurchaseRequestItemBody
                      ] !== item[field.key as keyof IPurchaseRequestItemBody]
                    : false
                }
              />
            ))}
          </>
        ) : (
          <p className="mb-1 italic text-gray-500">
            لم يتم العثور على معلومات للعنصر
          </p>
        )}
      </div>
    );
  },
);
ItemDetailsBox.displayName = 'ItemDetailsBox';

// Main component - memoized for performance
const DuplicateItemDialog = memo(
  ({
    isOpen,
    onOpenChange,
    item,
    onConfirm,
    onCancel,
    confirmButtonRef,
    duplicateItem,
  }: DuplicateItemDialogProps) => {
    // Memoize dialog handlers
    const handleCancel = useCallback(() => {
      onCancel();
    }, [onCancel]);

    const handleConfirm = useCallback(() => {
      onConfirm();
    }, [onConfirm]);

    // Early return if no item data
    if (!item) return null;

    // Render the dialog
    return (
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent
          className="m-0 flex flex-col justify-start gap-0 space-y-2 p-0"
          size="lg"
          hiddenCloseIcon={true}
        >
          <DialogTitle className="text-xl">
            <div className="flex w-full items-center justify-start px-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-200 p-2 shadow-2xl shadow-red-700 dark:bg-red-100">
                <TriangleAlert className="h-10 w-10 text-red-700" />
              </div>
              <div>
                <div className="p-2">
                  الصنف موجود بالفعل
                  <div className="flex items-center gap-1">
                    <DialogDescription className="flex gap-1">
                      هل تريد تعديل العنصر الموجود؟
                    </DialogDescription>
                  </div>
                </div>
              </div>
            </div>

            <DialogClose className="absolute left-3 top-3" asChild>
              <Button variant="destructive" size="icon" onClick={() => {}}>
                <X className="h-5 w-5" />
              </Button>
            </DialogClose>
            <Separator />
          </DialogTitle>

          <div className="relative grid grid-cols-2 gap-8 p-4 text-sm">
            {/* Duplicate Item Box (OLD ITEM) */}
            {duplicateItem && (
              <ItemDetailsBox
                title="العنصر الحالي"
                item={duplicateItem}
                compareItem={item}
                isNewItem={false}
              />
            )}

            {/* Arrow between boxes */}
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform">
              <div className="flex h-12 w-12 animate-pulse items-center justify-center rounded-full bg-red-100 p-2 shadow-lg dark:bg-red-950">
                <ArrowLeft className="h-12 w-12 text-red-600 dark:text-red-400" />
              </div>
            </div>

            {/* New Item Box */}
            <ItemDetailsBox
              title="العنصر الجديد"
              item={item}
              compareItem={duplicateItem}
              isNewItem={true}
            />
          </div>

          <Separator />
          <DialogFooter className="p-2">
            <Button variant="outline" onClick={handleCancel}>
              إلغاء
            </Button>
            <Button
              variant="destructive"
              ref={confirmButtonRef}
              onClick={handleConfirm}
              autoFocus
            >
              تعديل العنصر
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  },
);

DuplicateItemDialog.displayName = 'DuplicateItemDialog';

export default DuplicateItemDialog;
